// Who can do what (contract C3, spec §7.4). Pure: build a context once from the login
// payload plus the small reads the hook makes, then ask it questions. Components never
// re-derive scope themselves.
//
// Every GUID is trimmed and upper-cased before it is compared.

import { SCOPE_TYPES } from "./constants";

export const IMPLICIT_ALL_ROLES = [1];
export const IMPLICIT_DISTRICT_ROLES = [2, 8];
export const ADMIN_ROLES = [1, 2, 8];

const isNil = (v) => v === null || v === undefined;
export const normId = (v) => {
  if (isNil(v)) return null;
  const s = String(v).trim().toUpperCase();
  return s ? s : null;
};
const normList = (list) => {
  const out = [];
  const seen = new Set();
  for (const v of list || []) {
    const id = normId(typeof v === "object" && v !== null ? v.id ?? v.Id ?? v.SchoolDistrictId ?? v.UserId ?? v.SchoolId : v);
    if (id && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
};
const isDeleted = (row) => row && (row.IsDeleted === true || row.IsDeleted === 1 || row.IsDeleted === "1" || row.IsDeleted === "true");
const pick = (obj, ...keys) => {
  for (const k of keys) if (obj && !isNil(obj[k])) return obj[k];
  return undefined;
};

/**
 * login: LoginDetails ({userId, roleId, schoolDistrictId}; PascalCase tolerated)
 * additionalDistrictIds: active AdditionalSchoolDistrict ids for the user
 * portfolioDistrictIds: districts the user administers through a portfolio (AccountAllocation ->
 *   DistrictAllocation, read from vw_AccountAllocatedDistricts). Portfolio holders such as the
 *   Pomptonian District Admins have NO AdditionalSchoolDistrict rows, so own+additional alone
 *   collapses them to their home district.
 * grants: active EvalV2_EvaluatorGrant rows for the user
 * areaSites: { [AreaId]: SchoolId[] }
 * directReportIds: UserHierarchy?ManagerUserId= -> UserId[]
 */
export function buildContext({ login, additionalDistrictIds = [], portfolioDistrictIds = [], grants = [], areaSites = {}, directReportIds = [] } = {}) {
  const roleIdRaw = pick(login, "roleId", "RoleId");
  const roleId = Number.isFinite(Number(roleIdRaw)) && roleIdRaw !== "" && !isNil(roleIdRaw) ? Number(roleIdRaw) : null;
  const userId = normId(pick(login, "userId", "UserId"));
  const homeDistrictId = normId(pick(login, "schoolDistrictId", "SchoolDistrictId"));

  const districtIds = normList([homeDistrictId, ...(additionalDistrictIds || []), ...(portfolioDistrictIds || [])]);

  const areaMap = {};
  for (const [areaId, sites] of Object.entries(areaSites || {})) {
    const key = normId(areaId);
    if (key) areaMap[key] = normList(sites);
  }

  const normGrants = (grants || [])
    .filter((g) => g && !isDeleted(g))
    .map((g) => ({
      grantId: normId(g.GrantId ?? g.apiid),
      scopeType: String(g.ScopeType || "").trim(),
      schoolDistrictId: normId(g.SchoolDistrictId),
      schoolId: normId(g.SchoolId),
      areaId: normId(g.AreaId),
    }));

  return Object.freeze({
    userId,
    roleId,
    homeDistrictId,
    districtIds,
    grants: normGrants,
    areaSites: areaMap,
    directReportIds: normList(directReportIds),
    isSuperAdmin: roleId === 1,
    isDistrictAdmin: IMPLICIT_DISTRICT_ROLES.includes(roleId),
    isAdmin: ADMIN_ROLES.includes(roleId),
  });
}

/** {all:true} | {districtIds:[], schoolIds:[], userIds:[]} — upper-cased GUID strings. */
export function evaluableScope(ctx) {
  if (!ctx) return { districtIds: [], schoolIds: [], userIds: [] };
  if (IMPLICIT_ALL_ROLES.includes(ctx.roleId)) return { all: true };

  const districtIds = new Set();
  const schoolIds = new Set();
  const userIds = new Set();

  if (IMPLICIT_DISTRICT_ROLES.includes(ctx.roleId)) {
    for (const d of ctx.districtIds) districtIds.add(d);
  }

  for (const g of ctx.grants || []) {
    switch (g.scopeType) {
      case SCOPE_TYPES.DISTRICT:
        if (g.schoolDistrictId) districtIds.add(g.schoolDistrictId);
        break;
      case SCOPE_TYPES.SITE:
        if (g.schoolId) schoolIds.add(g.schoolId);
        break;
      case SCOPE_TYPES.AREA:
        for (const s of (g.areaId && ctx.areaSites[g.areaId]) || []) schoolIds.add(s);
        break;
      case SCOPE_TYPES.REPORTS:
        for (const u of ctx.directReportIds || []) userIds.add(u);
        break;
      default:
        break;
    }
  }

  return { districtIds: [...districtIds], schoolIds: [...schoolIds], userIds: [...userIds] };
}

function inScope(scope, { districtId, schoolId, userId }) {
  if (scope.all) return true;
  const d = normId(districtId);
  const s = normId(schoolId);
  const u = normId(userId);
  return (
    (d !== null && scope.districtIds.includes(d)) ||
    (s !== null && scope.schoolIds.includes(s)) ||
    (u !== null && scope.userIds.includes(u))
  );
}

/** user = vw_EvalV2_UserDirectory row. Never true for the user themself. */
export function canEvaluate(ctx, user) {
  if (!ctx || !user) return false;
  const targetId = normId(pick(user, "UserId", "userId"));
  if (!targetId || (ctx.userId && targetId === ctx.userId)) return false;
  return inScope(evaluableScope(ctx), {
    districtId: pick(user, "SchoolDistrictId", "schoolDistrictId"),
    schoolId: pick(user, "SchoolId", "schoolId"),
    userId: targetId,
  });
}

/**
 * evalRow = EvalV2_Evaluation / vw_EvalV2_Evaluations row. Own evaluations are always visible
 * (My Evaluations), the evaluator sees what they performed, otherwise the evaluee must be in scope.
 */
export function canViewEvaluation(ctx, evalRow) {
  if (!ctx || !evalRow) return false;
  const evaluatee = normId(evalRow.EvaluateeUserId);
  const evaluator = normId(evalRow.EvaluatorUserId);
  if (ctx.userId && (evaluatee === ctx.userId || evaluator === ctx.userId)) return true;
  return inScope(evaluableScope(ctx), {
    districtId: evalRow.SchoolDistrictId,
    // The table calls it EvaluateeSchoolId; vw_EvalV2_Evaluations / DashboardRows alias it SchoolId.
    schoolId: evalRow.EvaluateeSchoolId ?? evalRow.SchoolId,
    userId: evaluatee,
  });
}

/**
 * Should a vw_EvalV2_DashboardRows row count on this user's dashboard? Everything for Super
 * Admin; otherwise evaluations they performed plus evaluations of people in their evaluable
 * scope (district, granted site/area, direct reports) — but never their own evaluation as
 * the evaluee, which belongs on My Evaluations, not on their team's numbers.
 */
export function inDashboardScope(ctx, row) {
  if (!ctx || !row) return false;
  const scope = evaluableScope(ctx);
  if (scope.all) return true;
  const evaluatee = normId(row.EvaluateeUserId);
  if (ctx.userId && evaluatee === ctx.userId) return false;
  if (ctx.userId && normId(row.EvaluatorUserId) === ctx.userId) return true;
  return inScope(scope, {
    districtId: row.SchoolDistrictId,
    schoolId: row.EvaluateeSchoolId ?? row.SchoolId,
    userId: evaluatee,
  });
}

/** Own-record check used by My Evaluations and the in-app sign-off. */
export function isOwnEvaluation(ctx, evalRow) {
  if (!ctx || !evalRow || !ctx.userId) return false;
  return normId(evalRow.EvaluateeUserId) === ctx.userId;
}

export function canEditSystemTemplates(ctx) {
  return Boolean(ctx) && ctx.roleId === 1;
}

/** template null => "may this user create district templates at all?" */
export function canManageTemplates(ctx, template) {
  if (!ctx) return false;
  if (ctx.roleId === 1) return true;
  if (!ADMIN_ROLES.includes(ctx.roleId)) return false;
  if (template === null || template === undefined) return true;
  const isSystem = template.IsSystem === true || template.IsSystem === 1 || isNil(template.SchoolDistrictId);
  if (isSystem) return false;
  return ctx.districtIds.includes(normId(template.SchoolDistrictId));
}

/** schoolDistrictId omitted => "may this user manage evaluators anywhere?" */
export function canManageEvaluators(ctx, schoolDistrictId) {
  if (!ctx) return false;
  if (ctx.roleId === 1) return true;
  if (!IMPLICIT_DISTRICT_ROLES.includes(ctx.roleId)) return false;
  if (schoolDistrictId === undefined) return ctx.districtIds.length > 0;
  const d = normId(schoolDistrictId);
  return d !== null && ctx.districtIds.includes(d);
}

/** null = every district (Super Admin); else the district set admins may act in ([] for non-admins). */
export function adminDistrictIds(ctx) {
  if (!ctx) return [];
  if (ctx.roleId === 1) return null;
  if (IMPLICIT_DISTRICT_ROLES.includes(ctx.roleId)) return [...ctx.districtIds];
  return [];
}

/** Any evaluable scope at all: shows Dashboard / Records / Perform. */
export function isEvaluatorAtAll(ctx) {
  const scope = evaluableScope(ctx);
  if (scope.all) return true;
  return scope.districtIds.length > 0 || scope.schoolIds.length > 0 || scope.userIds.length > 0;
}

/** Districts an evaluator may pick in the scope picker: null = all, else explicit list (grants included). */
export function scopeDistrictIds(ctx) {
  const scope = evaluableScope(ctx);
  return scope.all ? null : [...scope.districtIds];
}

/**
 * Every district this user holds a grant in, whatever the grant's scope type. A Site, Area
 * or Reports grant never puts its district in evaluableScope().districtIds (that would widen
 * it to the whole district), but the district is still where that evaluator's data lives —
 * the dashboard and pickers need it to know which district to read and then narrow.
 */
export function grantDistrictIds(ctx) {
  return normList((ctx?.grants || []).map((g) => g.schoolDistrictId));
}
